import React, { createContext, useContext, useReducer } from 'react'

import { useTranslation } from 'next-i18next'

import {
  Form,
  ensureConditionalFieldValues,
  wishesForPage1,
  wishesForPage2,
  wishesForPage3,
  preparations,
} from '@dentalux/ui-library-core/custom/components/ana-toolbox'
import { useLocalStorage } from '@dentalux/ui-library-core/hooks'
import { InsuranceType } from '@dentalux/ui-library-core/type'

import { Anamnesis } from 'src/@types/Anamnesis'
import { LocalStorage } from 'src/@types/LocalStorage'
import { Gender, Person } from 'src/@types/Person'
import { useAnamnesisSourceMutation } from 'src/hooks/api/useAnamnesisSourceMutation'
import { AnaMode } from 'src/hooks/useAnaNavigation'

export enum ActionTypes {
  setAnswers = 'setAnswers',
  setRenew = 'setRenew',
  setNew = 'setNew',
  setEdit = 'setEdit',
  setLoading = 'setLoading',
  setSourceReferenceId = 'setSourceReferenceId',
  reset = 'reset',
}

type NewPayload = {
  language: string
  clinicReferenceId: string
  // Optional override. When provided, drives whether the FactoringAgreement
  // step is included in the form flow. When omitted, defaults to true
  // (the legacy behavior: assume factoring is required).
  factoringAgreementMissing?: boolean
  data: {
    firstName: string
    lastName: string
    email: string
    phone: string
    birthday: string
  }
}

type RenewPayload = {
  language: string
  clinicReferenceId: string
  factoringAgreementMissing: boolean
  referenceId: string
  data: Anamnesis
}

type EditPayload = RenewPayload

type AnswersPayload = Partial<Form>

type SetAnswersAction = { type: ActionTypes.setAnswers; payload: AnswersPayload }

type SetRenewAction = {
  type: ActionTypes.setRenew
  payload: RenewPayload
}

type SetNewAction = {
  type: ActionTypes.setNew
  payload: NewPayload
}

type SetLoadingAction = {
  type: ActionTypes.setLoading
  payload: boolean
}

type SetSourceReferenceIdAction = {
  type: ActionTypes.setSourceReferenceId
  payload: string
}

type SetEditAction = {
  type: ActionTypes.setEdit
  payload: EditPayload
}

type ResetAction = { type: ActionTypes.reset }

type State = {
  answers: Partial<Form>
  clinicReferenceId: string
  factoringAgreementMissing: boolean
  referenceId: string
  sourceReferenceId?: string
  loading: boolean
}

type Dispatch = (
  action:
    | SetAnswersAction
    | ResetAction
    | SetRenewAction
    | SetNewAction
    | SetEditAction
    | SetSourceReferenceIdAction
    | SetLoadingAction
) => void

const AnaContext = createContext<State | undefined>(undefined)

const AnaDispatchContext = createContext<Dispatch | undefined>(undefined)

const renewAnswers = (language: string, data: Anamnesis) => {
  const {
    person,
    diseases,
    medications,
    bodyWeightInKg,
    wishesAndExpectations,
    bodyHeightInCm,
    hasAllergies,
    lifeStyle,
    insurance,
  } = data

  return {
    language,
    gender: person.gender,
    firstName: person.firstName || '',
    lastName: person.lastName || '',
    birthday: loadDate(person.birthday || ''),
    placeOfBirth: person.placeOfBirth || '',

    street: person.address.street,
    zipCode: person.address.zipCode,
    country: person.address.country,
    city: person.address.city,

    email: person.email || '',
    phone: person.phone || '',

    filedForDifferentPerson: false,
    visitReason: '',

    type: InsuranceType[insurance.type],
    dentalInsuranceCompany: insurance.dentalInsuranceCompany || '',
    dentalInsuranceCompanyId: insurance.dentalInsuranceCompanyId || '',
    insuranceCompany: insurance.company || '',
    insuranceCompanyId: insurance.companyId || '',
    hasCareLevel: null,
    careLevelLimitedInTime: null,
    entitledToIntegrationAssistance: null,
    integrationAssistanceBasedOnType: null,
    hasFamilyInsurance: insurance.hasFamilyInsurance,
    livesOnDifferentAddress: insurance.livesOnDifferentAddress || false,
    hasCivilServiceAllowance: insurance.hasCivilServiceAllowance || false,
    hasDentalInsurance: insurance.hasDentalInsurance,

    holderFirstName: insurance.holderFirstName || '',
    holderLastName: insurance.holderLastName || '',
    holderBirthday: loadDate(insurance.holderBirthday || ''),
    holderStreet: insurance.holderStreet || '',
    holderZipcode: insurance.holderZipcode || '',
    holderCity: insurance.holderCity || '',

    isPregnant: person.gender !== Gender.Male ? null : false,
    occupation: lifeStyle.occupation || '',
    pregnantMonths: undefined,
    cigaretteConsumption: lifeStyle.cigaretteConsumption || undefined,
    alcoholConsumption: lifeStyle.alcoholConsumption || undefined,
    drugConsumption: lifeStyle.drugConsumption || undefined,

    bodyWeightInKg: bodyWeightInKg || null,
    bodyHeightInCm: bodyHeightInCm || null,

    diseases: diseases || [],
    hasAllergies: hasAllergies || 'UNKNOWN',
    medications: medications || [],
    wishesAndExpectations: [...wishesForPage1, ...wishesForPage2, ...wishesForPage3, ...preparations].map((woe) =>
      woe.key === 'has_bridges' ? wishesAndExpectations.find((el) => el.key === 'has_bridges') || woe : woe
    ),
    allergyPass: false,
  }
}

const newAnswers = (language: string, person: Person) => {
  return {
    language,
    hasFamilyInsurance: false,
    hasDentalInsurance: false,
    consumesAlcohol: false,
    consumesDrugs: false,
    medications: [],
    hasCareLevel: null,
    careLevelLimitedInTime: null,
    entitledToIntegrationAssistance: null,
    integrationAssistanceBasedOnType: null,
    isSmoker: false,
    allergyPass: false,
    hasCivilServiceAllowance: false,
    firstName: person.firstName,
    lastName: person.lastName,
    email: person.email,
    phone: person.phoneNumber,
    isPregnant: undefined,
    wishesAndExpectations: [...wishesForPage1, ...wishesForPage2, ...wishesForPage3, ...preparations],
    gender: person.gender,
    birthday: loadDate(person.birthday),
  }
}

const editAnswers = (language: string, data: Anamnesis) => {
  const { wishesAndExpectations, allergyPass, insurance } = data

  return {
    ...renewAnswers(language, data),
    language,
    wishesAndExpectations: [...wishesForPage1, ...wishesForPage2, ...wishesForPage3, ...preparations].map((woe) => {
      return wishesAndExpectations.find((el) => el.key === woe.key) || woe
    }),
    allergyPass,
    isPregnant: data.lifeStyle.isPregnant,
    hasCareLevel: insurance.hasCareLevel === null ? null : insurance.hasCareLevel ? 'true' : 'false',
    careLevelLimitedInTime:
      insurance.careLevelLimitedInTime === null ? null : insurance.careLevelLimitedInTime ? 'true' : 'false',
    entitledToIntegrationAssistance:
      insurance.entitledToIntegrationAssistance === null
        ? null
        : insurance.entitledToIntegrationAssistance
        ? 'true'
        : 'false',
    integrationAssistanceBasedOnType:
      insurance.integrationAssistanceBasedOnType === null ? null : insurance.integrationAssistanceBasedOnType,
    filedForDifferentPerson: false,
    visitReason: data.visitReason || '',
  }
}

export const defaultAnaState: State = {
  referenceId: '',
  sourceReferenceId: '',
  clinicReferenceId: '',
  factoringAgreementMissing: false,
  answers: {},
  loading: false,
}

export const loadDate = (date: string) => date?.split('-').reverse().join('.')

const unauthorizedReducer =
  (persist) =>
  (
    state: State,
    action:
      | SetAnswersAction
      | SetSourceReferenceIdAction
      | SetNewAction
      | SetEditAction
      | ResetAction
      | SetLoadingAction
      | SetRenewAction
  ) => {
    const finalize = (nextState: State) => {
      persist(nextState)
      return nextState
    }

    switch (action.type) {
      case ActionTypes.setAnswers: {
        return finalize({ ...state, answers: ensureConditionalFieldValues({ ...state.answers, ...action.payload }) })
      }

      case ActionTypes.reset:
        return finalize(defaultAnaState)

      case ActionTypes.setSourceReferenceId: {
        return finalize({ ...state, sourceReferenceId: action.payload })
      }

      case ActionTypes.setLoading:
        return finalize({ ...state, loading: action.payload })

      case ActionTypes.setRenew: {
        const { data, language, clinicReferenceId, factoringAgreementMissing, referenceId } = action.payload

        return finalize({
          factoringAgreementMissing,
          referenceId,
          clinicReferenceId,
          loading: false,
          answers: renewAnswers(language, data),
        })
      }

      case ActionTypes.setNew: {
        const { language, clinicReferenceId, data, factoringAgreementMissing } = action.payload

        return finalize({
          factoringAgreementMissing: factoringAgreementMissing ?? true,
          referenceId: '',
          clinicReferenceId,
          loading: false,
          answers: { language, wishesAndExpectations: [], ...data },
        })
      }

      case ActionTypes.setEdit: {
        const { referenceId, clinicReferenceId, data, language, factoringAgreementMissing } = action.payload

        return finalize({
          factoringAgreementMissing,
          referenceId,
          clinicReferenceId,
          loading: false,
          answers: editAnswers(language, data),
        })
      }

      default:
        throw new Error(`Unhandled action`)
    }
  }

export const AnaProvider = ({ children }) => {
  const [value, setValue] = useLocalStorage(LocalStorage.Ana, defaultAnaState)

  const [state, dispatch] = useReducer(unauthorizedReducer(setValue), { ...value })

  return (
    <AnaContext.Provider value={state}>
      <AnaDispatchContext.Provider value={dispatch}>{children}</AnaDispatchContext.Provider>
    </AnaContext.Provider>
  )
}

export const useAnaContext = () => {
  const context = useContext(AnaContext)

  if (context === undefined) {
    throw new Error('useAnaContext must be used within a useAnaContext')
  }

  return context
}

export const useInit = ({
  mode,
  factoringRequired,
}: {
  mode: AnaMode
  // When set, overrides whether the FactoringAgreement step is part of the
  // flow. Callers from /documents know this from the per-clinic doc state
  // (FACTORING_AGREEMENT.status === 'NOT_REQUIRED' means the clinic doesn't
  // need a factoring agreement at all). When undefined, fall back to legacy
  // behavior (derive from the loaded anamnesis's factoringAgreementResult).
  factoringRequired?: boolean
}) => {
  const dispatch = useAnaDispatch()

  const { sourceReferenceId } = useAnaContext()

  const { mutateAsync } = useAnamnesisSourceMutation()

  const {
    i18n: { language },
  } = useTranslation()

  return {
    initNew: async ({ clinicReferenceId, person }: { clinicReferenceId: string; person: Person }) => {
      dispatch(
        setNew({
          clinicReferenceId,
          language,
          factoringAgreementMissing: factoringRequired === false ? false : undefined,
          data: sourceReferenceId
            ? editAnswers(language, {
                ...(await mutateAsync({ anamnesisReferenceId: sourceReferenceId })),
                clinicReferenceId,
                referenceId: '',
              })
            : newAnswers(language, person),
        })
      )
    },
    initRenewOrEdit: async (ana: Anamnesis) => {
      const { factoringAgreementResult } = ana

      const { signed = false, valid = false, expired = false } = factoringAgreementResult ?? {}

      const payload = {
        language,
        clinicReferenceId: ana.clinicReferenceId,
        referenceId: String(ana.referenceId),
        data: sourceReferenceId
          ? {
              ...(await mutateAsync({ anamnesisReferenceId: sourceReferenceId })),
              clinicReferenceId: ana.clinicReferenceId,
              referenceId: ana.referenceId,
            }
          : ana,
        factoringAgreementMissing:
          factoringRequired === false ? false : expired || !signed || !valid,
      }

      dispatch((mode === AnaMode.RENEW ? setRenew : setEdit)(payload))
    },
  }
}

export const useAnaDispatch = () => {
  const context = useContext(AnaDispatchContext)

  if (context === undefined) {
    throw new Error('useAnaDispatch must be used within a useAnaContext')
  }

  return context
}

export const setAnswers = (payload: AnswersPayload): SetAnswersAction => ({
  type: ActionTypes.setAnswers,
  payload,
})

export const reset = (): ResetAction => ({
  type: ActionTypes.reset,
})

export const setRenew = (payload: RenewPayload): SetRenewAction => ({
  type: ActionTypes.setRenew,
  payload,
})

export const setNew = (payload: NewPayload): SetNewAction => ({
  type: ActionTypes.setNew,
  payload,
})

export const setEdit = (payload: EditPayload): SetEditAction => ({
  type: ActionTypes.setEdit,
  payload,
})

export const setLoading = (payload: boolean): SetLoadingAction => ({
  type: ActionTypes.setLoading,
  payload,
})

export const setSourceReferenceId = (payload: string): SetSourceReferenceIdAction => ({
  type: ActionTypes.setSourceReferenceId,
  payload,
})
